/**
 * ImageGalleryWithViewer - Componente reutilizable para galerías con visor
 * 
 * Incluye thumbnails con botones de acción, visor integrado y soporte completo móvil/desktop
 */

import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaSearchPlus, FaTrash } from 'react-icons/fa';
import ImageViewer from '../common/ImageViewer';

// Hook para detectar dispositivo móvil
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

const ImageGalleryWithViewer = ({
  images = [],
  title = "Fotos Ya Capturadas",
  onDeleteImage,
  disabled = false,
  viewerTitle = "Galería de Imágenes",
  showDeleteButton = true,
  className = ""
}) => {
  // Estados para el visor de imágenes
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const isMobile = useIsMobile();

  // Funciones para el visor de imágenes
  const openViewer = (index) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
  };

  // Manejar eliminación de imagen
  const handleDeleteImage = (e, imagenId) => {
    e.stopPropagation(); // Prevenir que se abra el visor
    if (onDeleteImage) {
      onDeleteImage(imagenId);
    }
  };

  if (images.length === 0) {
    return null;
  }

  return (
    <>
      <div className={`bg-gray-800 border border-gray-600 rounded-lg p-4 ${className}`}>
        <h4 className="text-white font-medium mb-3 flex items-center">
          <FaCheckCircle className="text-green-400 mr-2" />
          {title} ({images.length})
        </h4>
        <div className="flex gap-3">
          {images.map((imagen, index) => (
            <div key={imagen.id} className="flex-1">
              <div className="aspect-square bg-gray-700 rounded-lg overflow-hidden cursor-pointer relative group">
                <img
                  src={imagen.thumbnail_url || imagen.url_storage}
                  alt={`Imagen ${index + 1}`}
                  className="w-full h-full object-cover transition-transform group-hover:scale-110"
                  onClick={() => openViewer(index)}
                />
                <div className="absolute top-1 left-1 bg-green-600 text-white text-xs px-1.5 py-0.5 rounded">
                  {index + 1}
                </div>
                
                {/* Botones optimizados para móvil/desktop */}
                {isMobile ? (
                  /* Vista móvil: botones siempre visibles */
                  <>
                    {/* Botón de ver imagen - esquina inferior derecha */}
                    <button
                      type="button"
                      onClick={() => openViewer(index)}
                      className="absolute bottom-1 right-1 p-2 bg-blue-600 hover:bg-blue-700 rounded-full transition-colors shadow-lg"
                      title="Ver imagen ampliada"
                    >
                      <FaSearchPlus className="text-white" size={12} />
                    </button>
                    
                    {/* Botón de eliminar - esquina superior derecha */}
                    {showDeleteButton && onDeleteImage && (
                      <button
                        type="button"
                        onClick={(e) => handleDeleteImage(e, imagen.id)}
                        className="absolute top-1 right-1 p-1.5 bg-red-600 hover:bg-red-700 rounded-full transition-colors shadow-lg"
                        title="Eliminar imagen"
                        disabled={disabled}
                      >
                        <FaTrash className="text-white" size={10} />
                      </button>
                    )}
                  </>
                ) : (
                  /* Vista desktop: overlay con hover */
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Botón de eliminar - esquina superior derecha */}
                    {showDeleteButton && onDeleteImage && (
                      <button
                        type="button"
                        onClick={(e) => handleDeleteImage(e, imagen.id)}
                        className="absolute top-2 right-2 p-1.5 bg-red-600 hover:bg-red-700 rounded-full transition-colors"
                        title="Eliminar imagen"
                        disabled={disabled}
                      >
                        <FaTrash className="text-white" size={14} />
                      </button>
                    )}
                    
                    {/* Botón de ver - centro */}
                    <button
                      type="button"
                      onClick={() => openViewer(index)}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-3 bg-blue-600 hover:bg-blue-700 rounded-full transition-colors"
                      title="Ver imagen ampliada"
                    >
                      <FaSearchPlus className="text-white" size={16} />
                    </button>
                  </div>
                )}
              </div>
              {imagen.descripcion && (
                <p className="text-xs text-gray-400 mt-1 truncate">
                  {imagen.descripcion}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Visor de imágenes con z-index alto y fondo sólido */}
      <ImageViewer
        images={images}
        isOpen={viewerOpen}
        onClose={closeViewer}
        initialIndex={viewerIndex}
        title={viewerTitle}
        style={{ zIndex: 9999, marginTop: 0 }}
      />
    </>
  );
};

export default ImageGalleryWithViewer;